import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import useMemoryStore from '@/store/memory';
import useWorkStarterPrompts from '@/hooks/useWorkStarterPrompts';
import { mixWorkDiscoveryIdeas } from '@/constants/workDiscovery';
import { resolveWorkSuggestionPrompt } from '@/constants/work';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import IdeaGroup from './IdeaGroup';
import Text from '@/components/Typography/Text';
import RecentDiscovery from './RecentDiscovery';
import useDiscoveryExperience from '@/hooks/useDiscoveryExperience';
import { rankDiscoveryIdeas } from '@/lib/discoveryExperience';

const PAGE_SIZE = 6;
export default forwardRef(function WorkDiscoveryFeed({ enabled, onSelect, hasFiles = false }, ref) {
  const { theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const experience = useDiscoveryExperience(enabled);
  const userId = useAuthStore(s => s.user?.id);
  const revision = useMemoryStore(s => s.workStarterPromptsRevision);
  const { items: personalized } = useWorkStarterPrompts({ enabled, allowStale: false, discovery: true });
  const [day, setDay] = useState(() => Math.floor(Date.now() / 86400000));
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => setDay(Math.floor(Date.now() / 86400000)), Math.max(1, (day + 1) * 86400000 - Date.now()));
    return () => clearTimeout(timer);
  }, [day, enabled]);
  const [count, setCount] = useState(PAGE_SIZE);
  const [width, setWidth] = useState(0);
  useEffect(() => setCount(PAGE_SIZE), [userId, revision, enabled]);
  const ideas = useMemo(() => rankDiscoveryIdeas(mixWorkDiscoveryIdeas(personalized || [], day), experience.history).slice(0, 60).map(idea => ({ ...idea, previewSeed: day })), [personalized, day, experience.history]);
  const loadMore = () => setCount(n => Math.min(n + PAGE_SIZE, ideas.length));
  useImperativeHandle(ref, () => ({ loadMore }), [ideas.length]);
  if (!enabled) return null;
  return <View testID="work-discovery-feed" style={[styles.root, { borderTopColor: palette.border }]}>
    <View style={styles.header}><Text size="xl" weight="semibold" accessibilityRole="header">For you</Text></View>
    <RecentDiscovery mode="work" palette={palette} onUse={experience.record} />
    <View style={styles.grid} onLayout={event => setWidth(event.nativeEvent.layout.width)}>
      {Array.from({ length: Math.ceil(Math.min(count, ideas.length) / 6) }, (_, group) => <IdeaGroup key={group}
        items={ideas.slice(group * 6, Math.min(group * 6 + 6, count))} groupIndex={group} width={width} palette={palette}
        onSeen={experience.ready ? experience.record : undefined} onSelect={idea => { experience.record(idea, 'used'); onSelect(resolveWorkSuggestionPrompt(idea, { hasFiles })); }} />)}
    </View>
    {count >= ideas.length && <Text style={styles.more} size="s" color="secondary">You’re caught up. Come back later for fresh ideas.</Text>}
    {count < ideas.length && <Pressable accessibilityRole="button" accessibilityLabel="More Work ideas" onPress={loadMore} style={styles.more}><Text size="s" color="secondary">More Work ideas</Text></Pressable>}
  </View>;
});
const styles = StyleSheet.create({
  root: { width: '100%', maxWidth: 1080, alignSelf: 'center', borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 24, paddingHorizontal: 20, paddingBottom: 36 },
  header: { gap: 6, marginBottom: 20 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  more: { padding: 20, alignItems: 'center' },
});
